import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const slug = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || `p_${Date.now()}`;

function TierTable({ tiers, onChange }) {
  const upd = (i, k, v) => onChange(tiers.map((t, idx) => (idx === i ? { ...t, [k]: Number(v) } : t)));
  return (
    <table className="w-full text-sm" data-testid="apparel-sheet-tiers">
      <thead><tr className="text-[10px] font-mono uppercase text-slate-400 text-left"><th>Up to (pcs / sheets)</th><th>Price per sheet</th><th /></tr></thead>
      <tbody>{tiers.map((t, i) => (
        <tr key={i}>
          <td className="py-1 pr-2"><Input type="number" value={t.max} onChange={(e) => upd(i, "max", e.target.value)} className="h-8 rounded-sm num" data-testid={`apparel-tier-max-${i}`} /></td>
          <td className="py-1 pr-2"><Input type="number" step="0.01" value={t.price} onChange={(e) => upd(i, "price", e.target.value)} className="h-8 rounded-sm num" data-testid={`apparel-tier-price-${i}`} /></td>
          <td><button onClick={() => onChange(tiers.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button></td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function PlacementTable({ rows, onChange }) {
  const upd = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: k === "label" ? v : Number(v) } : r)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="apparel-placements-table">
        <thead><tr className="text-[10px] font-mono uppercase text-slate-400 text-left"><th>Placement</th><th>W"</th><th>H"</th><th>Stitches</th><th>Upcharge/pc</th><th /></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i}>
            <td className="py-1 pr-2"><Input value={r.label} onChange={(e) => upd(i, "label", e.target.value)} className="h-8 rounded-sm min-w-[120px]" data-testid={`apparel-pl-label-${i}`} /></td>
            {["w", "h", "stitches", "upcharge"].map((k) => (
              <td key={k} className="py-1 pr-2"><Input type="number" step="0.01" value={r[k]} onChange={(e) => upd(i, k, e.target.value)} className="h-8 w-20 rounded-sm num" data-testid={`apparel-pl-${k}-${i}`} /></td>
            ))}
            <td><button onClick={() => onChange(rows.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function ApparelSettingsCard({ s, setS }) {
  const tiers = s.apparel_sheet_tiers || [];
  const pls = s.apparel_placements || [];
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="apparel-settings">
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-head font-bold mb-1">Customer-supplied & gang sheet prices</h3>
        <p className="text-[11px] text-slate-500 mb-3">Price per {s.apparel_sheet_w}×{s.apparel_sheet_h}" sheet. For customer-supplied garments the tier uses the order's total pieces (one sheet per piece, more if the artwork is bigger); for gang sheets it uses the number of sheets.</p>
        <TierTable tiers={tiers} onChange={(v) => setS({ ...s, apparel_sheet_tiers: v })} />
        <Button variant="ghost" className="h-7 text-xs text-[#2495D3] mt-1" data-testid="apparel-tier-add" onClick={() => setS({ ...s, apparel_sheet_tiers: [...tiers, { max: 99999, price: 0 }] })}><Plus size={13} className="mr-1" />Tier</Button>
        <div className="mt-4">
          <Label className="text-xs">Big sizes (get the big-size upcharge)</Label>
          <Input data-testid="apparel-big-sizes" value={(s.apparel_big_sizes || []).join(", ")} onChange={(e) => setS({ ...s, apparel_big_sizes: e.target.value.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean) })} className="rounded-sm mt-1" />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-head font-bold mb-1">Print placements</h3>
        <p className="text-[11px] text-slate-500 mb-3">Default size (DTF) and stitches (embroidery) per placement, plus the extra charged per piece.</p>
        <PlacementTable rows={pls} onChange={(v) => setS({ ...s, apparel_placements: v })} />
        <Button variant="ghost" className="h-7 text-xs text-[#2495D3] mt-1" data-testid="apparel-pl-add" onClick={() => setS({ ...s, apparel_placements: [...pls, { key: slug(`p ${Date.now()}`), label: "", w: 4, h: 4, stitches: 5000, upcharge: 0 }] })}><Plus size={13} className="mr-1" />Placement</Button>
      </div>
    </div>
  );
}
